/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedNode').BalancedNode} BalancedNode */

import { useEffect, useState } from 'react'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { apiGet } from '../../generic/Api_Functions'
import { TaskInstruction } from '../workflow2.0/TaskInstruction'
import { useSmartTextArea } from '../../generic/smarttextarea/SmartTextArea'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { formatBalancedLabel } from '../../generic/utilities/objectStructures/balanced/balancedLabel'

/**
 * Right-panel info for the selected JOB node.
 *
 * Shows the node name, duration and the currently loaded task name, and loads
 * the `TaskInstruction` component for the selected task reference, following the
 * same loading pattern as `TaskInstructionWidget`. The full task is fetched on
 * demand via `apiGet('mos/taskReference/byId/{uuid}')`.
 *
 * The two chevron buttons navigate between the job's task references by
 * `taskOrder` (clamped to `1..taskReferences.length`). It starts on `taskOrder 1`.
 *
 * Mounted with `key={selectedNode.id}` by the parent so each selection gets
 * fresh editors and resets the navigation to the first task.
 *
 * @param {Object} props
 * @param {BalancedNode|null} props.selectedNode - The JOB node selected in the tree
 * @param {number}            props.workflowId   - ID of the root workflow, used to detect sub-workflow nodes
 * @param {{instructions: boolean, notes: boolean}} [props.collapsed] - Per-session collapse state of the Instructions/Notes sections
 * @param {Function}          [props.onToggleCollapse] - Called with 'instructions'|'notes' to toggle a section
 */
const BalancedFormJobInfo = ({ selectedNode, workflowId, collapsed = { instructions: false, notes: false }, onToggleCollapse = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [task, setTask] = useState(null)
  const [taskOrder, setTaskOrder] = useState(1)

  const { editor } = useSmartTextArea(task?.instructions)
  const notesEditor = useSmartTextArea(task?.notes)

  const isJob = selectedNode?.nodeType === 'JOB'
  const taskReferences = isJob ? (selectedNode.jobReference?.taskReferences ?? []) : []
  const maxTaskOrder = taskReferences.length
  const taskRef = taskReferences.find(t => t.taskOrder === taskOrder)

  // Extended business fields (HU-10): extendedColumn1/2/3 shown as Version/Revision/
  // Comments in order; empty ones are hidden and indexValue stays internal.
  const jobRef = selectedNode?.jobReference ?? {}
  const extendedFields = [
    { label: t_('Version'), value: jobRef.extendedColumn1 },
    { label: t_('Revision'), value: jobRef.extendedColumn2 },
    { label: t_('Comments'), value: jobRef.extendedColumn3 }
  ].filter(field => field.value != null && String(field.value).trim() !== '')

  useEffect(() => {
    /**
     * Fetches the full task object for the current task reference and stores it.
     * Resets `task` to null before fetching so the loading spinner is shown
     * while the request is in flight.
     */
    const loadTask = async () => {
      setTask(null)
      if (!taskRef?.uuid) return
      setTask(await apiGet('mos/taskReference/byId/' + taskRef.uuid))
    }
    loadTask()
  }, [taskRef?.uuid])

  /**
   * Renders the instructions panel for the currently selected task reference.
   *
   * Returns a "No instructions" message when no task reference is active,
   * a `CircularProgress` spinner while the task is loading (`task === null`),
   * or the full `TaskInstruction` component once the task has loaded.
   *
   * @returns {React.ReactNode}
   */
  const renderInstructions = () => {
    if (!taskRef) {
      return <Typography variant='body2' color='text.secondary'>{t_('No instructions')}</Typography>
    }
    if (task === null) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      )
    }
    return (
      <TaskInstruction
        editor={editor}
        notesEditor={notesEditor.editor}
        task={task}
        editing={false}
        balancedStyle
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
      />
    )
  }

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', padding: '0.5rem' }}>
      {!isJob
        ? (
          <Typography variant='body2' color='text.secondary'>{t_('No job selected')}</Typography>
          )
        : (
          <>
            <Typography variant='subtitle1' color='primary' align='center' style={{ fontWeight: 600 }}>
              {formatBalancedLabel(selectedNode.code, selectedNode.name)}
            </Typography>
            {workflowId !== selectedNode.currentWorkflow.id
              ? (
                <Typography>
                  {t_('Subworkflow') + ': ' + formatBalancedLabel(selectedNode.currentWorkflow.code, selectedNode.currentWorkflow.name)}
                </Typography>)
              : null}
            {extendedFields.map(field => (
              <Typography key={field.label} variant='body2'>
                {`${field.label}: ${field.value}`}
              </Typography>
            ))}
            {taskReferences.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.5rem' }}>
                <IconButton
                  size='small'
                  disabled={taskOrder <= 1}
                  onClick={() => setTaskOrder(o => Math.max(1, o - 1))}
                  aria-label={t_('Previous task')}
                >
                  <ChevronLeftIcon fontSize='small' />
                </IconButton>
                <Typography variant='body2' color='primary' noWrap style={{ flexGrow: 1, textAlign: 'center', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {task?.name ?? t_('Loading...')}
                </Typography>
                <IconButton
                  size='small'
                  disabled={taskOrder >= maxTaskOrder}
                  onClick={() => setTaskOrder(o => Math.min(maxTaskOrder, o + 1))}
                  aria-label={t_('Next task')}
                >
                  <ChevronRightIcon fontSize='small' />
                </IconButton>
              </Box>
            )}
            <div style={{ flexGrow: 1, minHeight: 0, marginTop: '0.5rem' }}>
              {renderInstructions()}
            </div>
          </>
          )}
    </div>
  )
}

export default BalancedFormJobInfo
